/**
 * 営業設定 > 販売代理店 (Sales Agent) サイクルタブ。
 *
 * 月次集計の締め日と、SA への支払いサイクルを設定する。
 * settings/sales.saAggregationDay / saPaymentCycle に保存される。
 */
import type { SaPaymentCycle } from '@goenhub/shared';
import { SA_PAYMENT_CYCLE_LABELS } from '@goenhub/shared';
import { useEffect, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input, Label } from '@/components/ui/input';
import { Spinner } from '@/components/ui/spinner';
import { toast } from '@/components/ui/toast';
import { useAdmins } from '@/hooks/useAdmins';
import {
  useSalesSettingsWithDefaults,
  useUpdateSalesSettings,
} from '@/hooks/useSettings';

const CYCLE_ORDER: ReadonlyArray<SaPaymentCycle> = [
  'eom_next_eom',
  'eom_2nd_next_eom',
  'd20_next_eom',
  'd20_2nd_10',
];

export function SaCycleTab() {
  const { merged, isLoading } = useSalesSettingsWithDefaults();
  const update = useUpdateSalesSettings();
  const { data: admins } = useAdmins();

  const [aggDay, setAggDay] = useState<string>('');
  const [cycle, setCycle] = useState<SaPaymentCycle>('eom_next_eom');
  const [defaultAssignee, setDefaultAssignee] = useState<string>('');
  const [seasonMode, setSeasonMode] = useState<'normal' | 'sale'>('normal');
  // v3.2.30: 契約・更新
  const [registrationFee, setRegistrationFee] = useState<string>('');
  const [renewalFee, setRenewalFee] = useState<string>('');
  const [defaultYears, setDefaultYears] = useState<string>('');
  const [reminderDays, setReminderDays] = useState<string>('');

  useEffect(() => {
    setAggDay(String(merged.saAggregationDay));
    setCycle(merged.saPaymentCycle);
    setDefaultAssignee(merged.defaultSalesAssigneeUid ?? '');
    setSeasonMode(merged.seasonMode === 'sale' ? 'sale' : 'normal');
    setRegistrationFee(String(merged.saRegistrationFee ?? 55000));
    setRenewalFee(String(merged.saRenewalFee ?? 33000));
    setDefaultYears(String(merged.saDefaultContractYears ?? 1));
    setReminderDays(String(merged.saRenewalReminderDays ?? 30));
  }, [
    merged.saAggregationDay,
    merged.saPaymentCycle,
    merged.defaultSalesAssigneeUid,
    merged.seasonMode,
    merged.saRegistrationFee,
    merged.saRenewalFee,
    merged.saDefaultContractYears,
    merged.saRenewalReminderDays,
  ]);

  // 社内の営業担当候補 (dept='sales' かつ level !== 'external' かつ active)
  const internalSalesAdmins = useMemo(() => {
    return (admins ?? [])
      .filter(
        (a) =>
          a.dept === 'sales' &&
          a.level !== 'external' &&
          a.resolvedStatus === 'active',
      )
      .sort((a, b) =>
        (a.displayName ?? a.email ?? a.uid).localeCompare(
          b.displayName ?? b.email ?? b.uid,
        ),
      );
  }, [admins]);

  const handleSave = async () => {
    const d = Number(aggDay);
    if (!Number.isFinite(d) || d < 1 || d > 31) {
      toast.error('集計サイクル日は 1〜31 の範囲で入力してください');
      return;
    }
    const regFee = Math.max(0, Math.floor(Number(registrationFee) || 0));
    const renFee = Math.max(0, Math.floor(Number(renewalFee) || 0));
    const years = Math.max(1, Math.floor(Number(defaultYears) || 1));
    const days = Math.max(0, Math.floor(Number(reminderDays) || 0));
    try {
      await update.mutateAsync({
        saAggregationDay: Math.floor(d),
        saPaymentCycle: cycle,
        defaultSalesAssigneeUid: defaultAssignee.trim() || null,
        seasonMode,
        saRegistrationFee: regFee,
        saRenewalFee: renFee,
        saDefaultContractYears: years,
        saRenewalReminderDays: days,
      });
      toast.success('保存しました');
    } catch (err) {
      toast.error(`失敗: ${err instanceof Error ? err.message : String(err)}`);
    }
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="max-w-lg space-y-5">
      {/* 季節モード切替 (v3.2.23+) — 全ブランドの商品カタログ + 報酬セットを一斉切替 */}
      <div className="rounded-md border-2 border-[var(--accent)] bg-[var(--accent-subtle)]/40 p-4 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <Label className="text-sm font-semibold !mb-0">
              現在の季節モード
            </Label>
            <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
              全ブランドの商品カタログ・報酬セットがこの設定に従って一斉切替されます
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setSeasonMode('normal')}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                seasonMode === 'normal'
                  ? 'bg-[var(--accent)] text-white border-[var(--accent)] font-semibold'
                  : 'bg-[var(--bg-input)] border-[var(--border)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              通常期
            </button>
            <button
              type="button"
              onClick={() => setSeasonMode('sale')}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                seasonMode === 'sale'
                  ? 'bg-amber-600 text-white border-amber-600 font-semibold'
                  : 'bg-[var(--bg-input)] border-[var(--border)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              セール期
            </button>
          </div>
        </div>
        <p className="text-[10px] text-[var(--text-muted)]">
          切替後の effective 判定:
          各ブランドの「isActive=true かつ season={'<選択した季節>'}」のカタログ →
          そのカタログの linkedCommissionSetId が effective な報酬セットになります。
        </p>
      </div>

      <div>
        <Label htmlFor="sa-agg-day">集計サイクル（毎月 N 日締）</Label>
        <div className="flex items-center gap-2">
          <span className="text-sm text-[var(--text-muted)]">毎月</span>
          <Input
            id="sa-agg-day"
            type="number"
            inputMode="numeric"
            min={1}
            max={31}
            value={aggDay}
            onChange={(e) => setAggDay(e.target.value)}
            className="max-w-[120px]"
          />
          <span className="text-sm text-[var(--text-muted)]">日</span>
        </div>
        <p className="text-xs text-[var(--text-muted)] mt-1">
          販売代理店の月次売上集計の締め日。31 = 月末締扱い（28〜31 を末日として処理）。
        </p>
      </div>

      <div>
        <Label>支払いサイクル</Label>
        <div className="flex flex-col gap-1.5 mt-1">
          {CYCLE_ORDER.map((c) => (
            <label
              key={c}
              className={`cursor-pointer text-sm py-2 px-3 border rounded-sm transition-colors ${
                cycle === c
                  ? 'border-[var(--accent)] bg-[var(--accent-subtle)]'
                  : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
              }`}
            >
              <input
                type="radio"
                name="sa-payment-cycle"
                value={c}
                checked={cycle === c}
                onChange={() => setCycle(c)}
                className="mr-2"
              />
              {SA_PAYMENT_CYCLE_LABELS[c]}
            </label>
          ))}
        </div>
      </div>

      <div className="border-t border-[var(--border)] pt-5">
        <Label htmlFor="default-sa-assignee">
          SA リファラル経由の Deal の既定担当者 (営業事務)
        </Label>
        <select
          id="default-sa-assignee"
          value={defaultAssignee}
          onChange={(e) => setDefaultAssignee(e.target.value)}
          className="w-full max-w-md mt-1 px-3 py-2 text-sm rounded-sm border border-[var(--border)] bg-[var(--bg-input)]"
        >
          <option value="">未設定 (Deal は未アサインで作成)</option>
          {internalSalesAdmins.map((a) => (
            <option key={a.uid} value={a.uid}>
              {a.displayName ?? a.email ?? a.uid}
              {a.email ? ` (${a.email})` : ''}
            </option>
          ))}
        </select>
        <p className="text-xs text-[var(--text-muted)] mt-1.5">
          SA リファラルリンク (
          <code className="px-1 rounded bg-[var(--bg-input)]">?sa=</code>) 経由で
          フォーム送信があった際、Deal の「担当者」(社内営業事務) に自動アサインする admin です。
          書類発行・受発注追跡・お客様対応の社内窓口を担う方を指定してください。
          未設定の場合は Deal が「未アサイン」で作成され、後から手動割当が必要になります。
        </p>
        {internalSalesAdmins.length === 0 && (
          <p className="text-xs text-amber-600 mt-1.5">
            候補者がいません: dept=sales かつ level !== external (社内営業) かつ status=active の admin を作成してください。
          </p>
        )}
      </div>

      {/* v3.2.30: 契約・更新 */}
      <div className="border-t border-[var(--border)] pt-5 space-y-4">
        <div>
          <Label className="text-sm font-semibold">契約・更新</Label>
          <p className="text-[11px] text-[var(--text-muted)] mt-0.5">
            登録料・更新料は SA への案内メール ({'{{fee}}'}) に差し込まれます。
            既定契約年数は新規登録時の初期値、リマインダー日数は契約終了の何日前に更新案内を自動送信するかです。
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label htmlFor="sa-reg-fee">SA 登録料 (円・税込)</Label>
            <Input
              id="sa-reg-fee"
              type="number"
              inputMode="numeric"
              min={0}
              value={registrationFee}
              onChange={(e) => setRegistrationFee(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="sa-renew-fee">SA 更新料 (円・税込)</Label>
            <Input
              id="sa-renew-fee"
              type="number"
              inputMode="numeric"
              min={0}
              value={renewalFee}
              onChange={(e) => setRenewalFee(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="sa-default-years">既定契約年数</Label>
            <Input
              id="sa-default-years"
              type="number"
              inputMode="numeric"
              min={1}
              max={10}
              value={defaultYears}
              onChange={(e) => setDefaultYears(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="sa-reminder-days">更新リマインダー (終了 N 日前)</Label>
            <Input
              id="sa-reminder-days"
              type="number"
              inputMode="numeric"
              min={0}
              value={reminderDays}
              onChange={(e) => setReminderDays(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div className="pt-2">
        <Button
          type="button"
          variant="primary"
          onClick={handleSave}
          disabled={update.isPending}
        >
          {update.isPending ? '保存中…' : '保存'}
        </Button>
      </div>
    </div>
  );
}
